/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import download from "downloadjs";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import {
  ReportType,
  CreateReportInput,
  useGetEventActionReportsLazyQuery,
} from "~/generated/graphql";
import { PopoverControls } from "~/components";
import { ROUTES } from "~/routing/routes";
import { showError, showSuccess } from "~/services/toast";
import { generateActionSuccessText } from "~/tools";
import { EventDetailsParams } from "~/views/events/EventsDetails/types";
import { useCreateReport } from "~/views/resources/hooks/useCreateReport/useCreateReport";

type Props = {
  eventActionId: string;
};

export function ReportDownload({ eventActionId }: Props) {
  const { eventId } = useParams<EventDetailsParams>();
  const [createReport] = useCreateReport();

  const [getReport, { loading }] = useGetEventActionReportsLazyQuery({
    onCompleted: (data) => {
      const fileName = `${eventId}_${eventActionId}_report`;
      if (data?.eventActionReports) {
        download(
          JSON.stringify(data?.eventActionReports),
          fileName,
          "application/json"
        );
      }
      showSuccess(generateActionSuccessText(fileName)()("downloaded")());
    },
    onError: (error) => {
      showError(error.message);
    },
  });

  const handleGetReport = () =>
    getReport({ variables: { eventActionId, eventId } });

  const handleGenerateCSVReport = async () => {
    const mutationInput: CreateReportInput = {
      reportType: ReportType.EventActionReportCsvExport,
      eventActionInput: {
        eventId,
        eventActionId,
      },
    };
    // to prevent blocked pop-up by browsers
    const newTab = window.open("", "_blank");
    try {
      const reportId = await createReport(mutationInput);
      if (newTab?.location && reportId) {
        // eslint-disable-next-line fp/no-mutation
        newTab.location.href = `${ROUTES.resources.reports}?newReportId=${reportId}`;
      }
    } catch (error) {
      newTab?.close();
    }
  };

  const menuItems = [
    {
      label: `JSON`,
      subLabel: "Full report including log output",
      onClick: handleGetReport,
    },
    {
      label: "CSV",
      subLabel: "Update data only, no log output data",
      onClick: handleGenerateCSVReport,
    },
  ];

  return (
    <PopoverControls
      size="large"
      items={menuItems}
      disabled={loading}
      placement="bottom"
      position="end"
      trigger={
        <Button severity="low" icon="chevronDown" iconRight>
          Get Report
        </Button>
      }
    />
  );
}
